import { useMemo, useState, Fragment } from 'react';
import {
  Box, Typography, Stack, Chip, Table, TableBody, TableCell, TableHead, TableRow,
  Paper, Alert, CircularProgress, Link, Tooltip, TextField, InputAdornment,
  IconButton, Collapse, MenuItem, Select, Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

// FEAT-021 — 「우리 기준」 탭. RCCS 준수 항목 카탈로그.
//
// 🔴 이 탭과 「제품별 현황」은 **다른 질문**에 답한다.
//   여기: 무엇을 지켜야 하고 어떻게 확인하는가 (기준이 바뀔 때 갱신)
//   저기: 지금 누가 통과하는가 (매주 갱신)
//   섞으면 "기준이 없는 것" 과 "기준은 있는데 제품이 못 지킨 것" 이 구별되지 않는다.
//
// 🔴 축 라벨을 여기에 박지 않는다. 피드의 legend 를 읽는다.
//   박아 두면 기준이 바뀔 때 화면이 따라오지 못하고, 그때 사람들은 화면을 믿는다.
//   legend 에 없는 값이 오면 그 값을 그대로 보여준다 — 화면이 임의로 해석하지 않는다.

const SEV_COLOR = { critical: 'error', high: 'error', medium: 'warning', low: 'default' };
const ALL = '__all__';

// 🔴 미배선 probe 중 '진짜 결함' 은 사람 판단(human)·권한 대기(blocked) 가 아닌 것뿐이다.
//   셋을 섞으면 멀쩡한 항목이 문제로 보이고 정작 진짜 공백이 묻힌다.
const isRealGap = (p) => !p.wired && p.not_wired_why !== 'human' && p.not_wired_why !== 'blocked';

function statusChips(status) {
  if (!status || Object.keys(status).length === 0) return null;
  const order = ['pass', 'fail', 'unknown', 'na'];
  const color = { pass: 'success', fail: 'error', unknown: 'default', na: 'default' };
  const label = { pass: '준수', fail: '미준수', unknown: '확인 불가', 'na': '해당 없음' };
  return order
    .filter((k) => status[k])
    .map((k) => (
      <Chip key={k} label={`${label[k]} ${status[k]}`} size="small" color={color[k]}
            variant={k === 'fail' ? 'filled' : 'outlined'} sx={{ fontSize: 10, height: 18 }} />
    ));
}

function ItemRow({ item, legend }) {
  const [open, setOpen] = useState(false);
  const probes = item.probes || [];
  const gaps = probes.filter(isRealGap);
  // legend 에 없는 값이면 원값을 그대로 — 화면이 해석하지 않는다.
  const lab = (axis, v) => legend?.[axis]?.[v] || v || '—';

  return (
    <Fragment>
      <TableRow hover sx={{ '& > *': { borderBottom: open ? 'unset' : undefined } }}>
        <TableCell sx={{ width: 34, p: 0.5 }}>
          <IconButton size="small" onClick={() => setOpen((v) => !v)} aria-label={open ? '접기' : '펼치기'}>
            {open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
          {item.id}
        </TableCell>
        <TableCell>
          <Typography variant="body2">{item.title}</Typography>
          {item.why && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {item.why}
            </Typography>
          )}
        </TableCell>
        <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
          {lab('scope', item.scope)}
          {/* '혼합' 이라는 말만으로는 읽히지 않는다 — 몇 대 몇인지 함께 보여준다. */}
          {item.scope_count && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10 }}>
              중앙 {item.scope_count.central ?? 0} · 제품 {item.scope_count.product ?? 0}
            </Typography>
          )}
        </TableCell>
        <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
          {item.applies?.all ? (
            '전 제품'
          ) : (
            <Tooltip
              arrow
              title={`조건부 적용 — ${[
                item.applies?.d_min && `데이터 등급 ${item.applies.d_min} 이상`,
                item.applies?.flags_any?.length && `해당 플래그: ${item.applies.flags_any.join(', ')}`,
              ].filter(Boolean).join(' · ')}`}
            >
              <Chip label="조건부" size="small" variant="outlined" sx={{ fontSize: 10, height: 19 }} />
            </Tooltip>
          )}
        </TableCell>
        <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{lab('how', item.how_main)}</TableCell>
        <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{lab('cycle', item.cycle_main)}</TableCell>
        <TableCell>
          <Chip label={item.severity || '—'} size="small" color={SEV_COLOR[item.severity] || 'default'}
                variant="outlined" sx={{ fontSize: 10, height: 19 }} />
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.4 }}>
            {statusChips(item.status)}
            {gaps.length > 0 && (
              <Tooltip title="정의만 되고 실제로 돌지 않는 점검이 있습니다" arrow>
                <Chip label={`🔴 배선 누락 ${gaps.length}`} size="small" color="error" sx={{ fontSize: 10, height: 18 }} />
              </Tooltip>
            )}
          </Stack>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell sx={{ py: 0, borderBottom: open ? undefined : 'none' }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1.5, pl: 4 }}>
              <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" fontWeight={700}>점검 수단 {probes.length}개</Typography>
                <Typography variant="caption" color="text.secondary">
                  담당 {item.owner || '—'}
                  {item.clauses?.length ? ` · 조항 ${item.clauses.join(' · ')}` : ''}
                </Typography>
              </Stack>
              <Paper variant="outlined" sx={{ borderRadius: 0, overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 640 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>수단</TableCell>
                      <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>확인 방식</TableCell>
                      <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>주기</TableCell>
                      <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>게이트</TableCell>
                      <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>배선</TableCell>
                      <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>실측</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {probes.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace', display: 'block' }}>
                            {p.id}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">{p.desc}</Typography>
                          {p.reuse && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10 }}>
                              ↪ {p.reuse} 의 판정을 인용
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>{lab('how', p.how)}</TableCell>
                        <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>{lab('cycle', p.cycle)}</TableCell>
                        <TableCell sx={{ fontSize: 11 }}>
                          {p.gate === 'block'
                            ? <Chip label="차단" size="small" color="error" sx={{ fontSize: 10, height: 18 }} />
                            : <Typography variant="caption" color="text.secondary">경고</Typography>}
                        </TableCell>
                        <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                          {/* 🔴 '안 도는 것' 과 '그렇게 설계된 것' 을 구분한다. */}
                          {p.wired
                            ? <Typography variant="caption" color="success.main">자동</Typography>
                            : isRealGap(p)
                              ? <Chip label="🔴 배선 누락" size="small" color="error" sx={{ fontSize: 10, height: 18 }} />
                              : <Typography variant="caption" color="text.secondary">
                                  {lab('how', p.not_wired_why)}
                                </Typography>}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.4} flexWrap="wrap" sx={{ gap: 0.3 }}>
                            {statusChips(p.status) || <Typography variant="caption" color="text.disabled">—</Typography>}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
}

export default function ComplianceCatalogTab({ state }) {
  const { data, error, loading, hint } = state;
  const [q, setQ] = useState('');
  const [fDomain, setFDomain] = useState(ALL);
  const [fScope, setFScope] = useState(ALL);
  const [fHow, setFHow] = useState(ALL);
  const [fCycle, setFCycle] = useState(ALL);
  const [fSev, setFSev] = useState(ALL);
  const [fApplies, setFApplies] = useState(ALL);

  const items = data?.items || [];
  const legend = data?.legend;

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((it) => {
      if (fDomain !== ALL && !String(it.domain || '').startsWith(fDomain)) return false;
      if (fScope !== ALL && it.scope !== fScope) return false;
      if (fHow !== ALL && it.how_main !== fHow) return false;
      if (fCycle !== ALL && it.cycle_main !== fCycle) return false;
      if (fSev !== ALL && it.severity !== fSev) return false;
      if (fApplies === 'all' && !it.applies?.all) return false;
      if (fApplies === 'cond' && it.applies?.all) return false;
      if (term && !`${it.id} ${it.title} ${it.why || ''} ${(it.clauses || []).join(' ')}`.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [items, q, fDomain, fScope, fHow, fCycle, fSev, fApplies]);

  const gapCount = useMemo(
    () => items.reduce((n, it) => n + (it.probes || []).filter(isRealGap).length, 0),
    [items],
  );

  if (loading) {
    return (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 2 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">준수 항목 카탈로그를 불러오는 중…</Typography>
      </Stack>
    );
  }
  if (error) {
    return (
      <Alert severity="error">
        <Typography variant="body2" fontWeight={600}>{error}</Typography>
        {hint && <Typography variant="caption">{hint}</Typography>}
      </Alert>
    );
  }
  if (items.length === 0) {
    return <Alert severity="info">준수 항목이 없습니다 — 카탈로그가 아직 생성되지 않았을 수 있습니다.</Alert>;
  }

  const std = data.standard || {};
  const sel = (label, value, setter, options) => (
    <Select
      size="small"
      value={value}
      onChange={(e) => setter(e.target.value)}
      sx={{ fontSize: 12, minWidth: 120 }}
      displayEmpty
    >
      <MenuItem value={ALL} sx={{ fontSize: 12 }}>{label} 전체</MenuItem>
      {options.map(([v, l]) => <MenuItem key={v} value={v} sx={{ fontSize: 12 }}>{l}</MenuItem>)}
    </Select>
  );
  const axisOpts = (axis) => Object.entries(legend?.[axis] || {});

  return (
    <Stack spacing={2}>
      {/* ── 기준 규모 ─────────────────────────────────────
          🔴 정본 조항 수와 별도 관리 조항 수를 더하지 않는다. 피드가 둘을 분리해
             담고 각각 설명을 붙여 보내므로, 그 설명을 그대로 쓴다. */}
      <Paper variant="outlined" sx={{ borderRadius: 0, p: 1.5 }}>
        <Stack direction="row" spacing={2.5} flexWrap="wrap" sx={{ gap: 1.5 }}>
          <Metric label="준수 항목" value={std.items} />
          <Metric label="조항" value={std.clauses} note={std.clauses_label} />
          {std.iso_clauses != null && <Metric label="별도 조항" value={std.iso_clauses} note={std.iso_clauses_label} />}
          <Metric label="점검 수단" value={std.probes} />
          <Metric label="자동" value={std.probes_auto} />
          <Metric label="사람 판단" value={std.probes_human} note="결함이 아닙니다 — 그렇게 설계된 항목입니다" />
          <Metric label="권한 대기" value={std.probes_blocked} note="결함이 아닙니다 — 권한이 없어 볼 수 없는 항목입니다" />
          <Metric
            label="🔴 배선 누락"
            value={std.probes_unwired ?? gapCount}
            note="정의만 되고 실제로 돌지 않는 점검 — 이것만이 결함입니다"
            emphasize={(std.probes_unwired ?? gapCount) > 0}
          />
        </Stack>
      </Paper>

      {/* ── 필터 ─────────────────────────────────────── */}
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
        <TextField
          size="small"
          placeholder="항목·조항 검색"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ width: { xs: '100%', sm: 220 } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        {sel('도메인', fDomain, setFDomain, (data.domains || []).map((d) => [d.key, d.title]))}
        {sel('누가', fScope, setFScope, axisOpts('scope'))}
        {sel('누구에게', fApplies, setFApplies, [['all', '전 제품'], ['cond', '조건부']])}
        {sel('어떻게', fHow, setFHow, axisOpts('how'))}
        {sel('주기', fCycle, setFCycle, axisOpts('cycle'))}
        {sel('심각도', fSev, setFSev, [['critical', 'critical'], ['high', 'high'], ['medium', 'medium'], ['low', 'low']])}
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
          {filtered.length} / {items.length}건
        </Typography>
        {[fDomain, fScope, fHow, fCycle, fSev, fApplies].some((v) => v !== ALL) || q ? (
          <Button
            size="small"
            onClick={() => { setQ(''); setFDomain(ALL); setFScope(ALL); setFHow(ALL); setFCycle(ALL); setFSev(ALL); setFApplies(ALL); }}
          >
            필터 해제
          </Button>
        ) : null}
      </Stack>

      {/* ── 항목 표 ──────────────────────────────────── */}
      <Paper variant="outlined" sx={{ borderRadius: 0, overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 980 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 34 }} />
              <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>항목</TableCell>
              <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>내용</TableCell>
              <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>누가 하는가</TableCell>
              <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>누구에게</TableCell>
              <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>어떻게 보는가</TableCell>
              <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>얼마나 자주</TableCell>
              <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>심각도</TableCell>
              <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>실측</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((it) => <ItemRow key={it.id} item={it} legend={legend} />)}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} sx={{ textAlign: 'center', color: 'text.secondary', py: 3 }}>
                  조건에 맞는 항목이 없습니다.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="caption" color="text.secondary">
        기준 {data.as_of || data.generated || '—'} · 주 1회 갱신 ·{' '}
        <Link href={`https://github.com/${data.sourceRepo}/blob/main/${data.sourcePath}`} target="_blank" rel="noreferrer">
          카탈로그 <OpenInNewIcon sx={{ fontSize: 11, verticalAlign: 'middle' }} />
        </Link>
      </Typography>
    </Stack>
  );
}

function Metric({ label, value, note, emphasize }) {
  const body = (
    <Box sx={{ minWidth: 88 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
      <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2, color: emphasize ? 'error.main' : 'text.primary' }}>
        {value ?? '—'}
      </Typography>
    </Box>
  );
  return note ? <Tooltip title={note} arrow>{body}</Tooltip> : body;
}
